import * as DocumentPicker from 'expo-document-picker';
import { File, Paths } from 'expo-file-system';
import { useFocusEffect, useRouter } from 'expo-router';
import * as Sharing from 'expo-sharing';
import { useSQLiteContext } from 'expo-sqlite';
import {
  BellRing,
  Bot,
  CalendarDays,
  ChevronRight,
  Check,
  CheckCircle2,
  CloudSun,
  Download,
  FileImage,
  Home,
  Key,
  ArrowDown,
  ArrowUp,
  LayoutGrid,
  Package,
  Plus,
  Upload,
  User,
  Mail,
  Receipt,
  RefreshCw,
  Rocket,
  RotateCcw,
  Search,
  Sparkles,
  Target,
  Users,
} from 'lucide-react-native';
import { ComponentType, useCallback, useEffect, useRef, useState } from 'react';
import {
  Modal,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  View,
} from 'react-native';

import { webTransition } from '@/lib/hover-style';
import { webNoOutline } from '@/lib/web-style';

import { useConfirm } from '@/components/ConfirmDialog';
import { ReplanSummaryModal } from '@/components/ReplanSummaryModal';
import { checkAndApplyUpdate, getVersionInfo } from '@/lib/app-updates';
import { DigestPreview } from '@/components/DigestPreview';
import { Fonts, Palette, Radius, Spacing } from '@/constants/theme';
import { registerScrollToTop } from '@/lib/scroll-bus';
import { now } from '@/db/ids';
import type { PropertyRow } from '@/db/types';
import { Platform } from 'react-native';
import { useIsDesktop } from '@/hooks/use-is-desktop';
import { backupFilename, exportBackup, restoreBackup } from '@/lib/backup';
import { bumpDataRevision } from '@/lib/data-revision';
import { addDocument } from '@/lib/documents';
import { listNotes } from '@/lib/notes';
import { listQuotes } from '@/lib/quotes';
import { WIDGETS, parseWidgetKeys } from '@/lib/sidebar-widgets';
import { buildWeeklyDigest, type DigestData } from '@/lib/digest';
import { exportTasksToIcs } from '@/lib/ical';
import { exportBinderPdf } from '@/lib/pdf';
import { listContacts } from '@/lib/contacts';
import { listDocumentsForProperty } from '@/lib/documents';
import { listInventory } from '@/lib/inventory';
import {
  getActiveProperty,
  getActiveTasks,
  getServiceHistoryForProperty,
  getSubtypeCoverageSignal,
  getUnconfirmedSeasonalCount,
  getSystemsForProperty,
  profileAttributeChecks,
  regenerateTasksForProperty,
  regenerateTasksWithDiff,
  type ReplanDiff,
  resetPropertyData,
} from '@/lib/tasks';
import { useOnboarding } from '@/store/onboarding';

// Note: confetti_enabled and show_preloader were once toggleable but are
// now always-on. Their DB columns remain (so older rows don't error on
// write) but Settings no longer exposes a row for them.
type PrefKey =
  | 'show_weather'
  | 'ask_expense_cost'
  | 'show_message_ticker'
  | 'show_quick_toolbar'
  | 'show_daily_calendar'
  | 'show_projects_on_today'
  | 'monthly_task_mode';

interface ToolDef {
  key: string;
  label: string;
  Icon: ComponentType<{ size?: number; color?: string; strokeWidth?: number }>;
  bg: string;
  onPress: () => void | Promise<void>;
}

// Module-level so the unlock persists across navigations within a session
// (closing the app and reopening will re-lock — a feature, since a friend
// re-installing the APK shouldn't inherit your unlock).
let adminUnlockedRef = false;

export default function Settings() {
  const router = useRouter();
  const db = useSQLiteContext();
  const confirm = useConfirm();
  const isDesktop = useIsDesktop();
  const settingsScrollRef = useRef<ScrollView | null>(null);
  const resetScroll = useCallback(() => {
    settingsScrollRef.current?.scrollTo({ y: 0, animated: false });
  }, []);
  useFocusEffect(useCallback(() => {
    resetScroll();
  }, [resetScroll]));
  useEffect(() => registerScrollToTop(resetScroll), [resetScroll]);
  const [working, setWorking] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  // Replan preview modal — same modal Tools / My Home / Edit Home use,
  // so the UX is identical everywhere a replan happens.
  const [replanSummary, setReplanSummary] = useState<ReplanDiff | null>(null);
  const [replanLoading, setReplanLoading] = useState(false);
  const [replanHomeName, setReplanHomeName] = useState<string>('this home');
  const [replanPropertyId, setReplanPropertyId] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [property, setProperty] = useState<PropertyRow | null>(null);
  // Tune badge — same computation as My Home / Today so the submenu
  // affordance is identical across tabs. Counts blank profile fields
  // PLUS systems missing a subtype pick.
  const [tuneBadge, setTuneBadge] = useState<number>(0);
  const [digestPreview, setDigestPreview] = useState<DigestData | null>(null);
  const [adminUnlocked, setAdminUnlocked] = useState(adminUnlockedRef);
  const [tapCount, setTapCount] = useState(0);
  const [nameOpen, setNameOpen] = useState(false);
  const [editFirst, setEditFirst] = useState('');
  const [editLast, setEditLast] = useState('');
  const [greetingOpen, setGreetingOpen] = useState(false);
  // Wave 69: disabled sidebar widgets collapse behind an expander.
  const [sidebarPickerOpen, setSidebarPickerOpen] = useState(false);

  const setGreetingSource = async (src: 'first_name' | 'home_name') => {
    if (!property) return;
    setProperty({ ...property, greeting_source: src });
    setGreetingOpen(false);
    await db.runAsync(
      `UPDATE property SET greeting_source = ?, updated_at = ? WHERE id = ?`,
      src,
      now(),
      property.id
    );
  };

  const openNameEditor = () => {
    setEditFirst(property?.owner_first_name ?? '');
    setEditLast(property?.owner_last_name ?? '');
    setNameOpen(true);
  };

  const saveName = async () => {
    if (!property) return;
    const f = editFirst.trim() || null;
    const l = editLast.trim() || null;
    setProperty({ ...property, owner_first_name: f, owner_last_name: l });
    setNameOpen(false);
    await db.runAsync(
      `UPDATE property SET owner_first_name = ?, owner_last_name = ?, updated_at = ? WHERE id = ?`,
      f,
      l,
      now(),
      property.id
    );
  };

  const flash = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(null), 3000);
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const p = await getActiveProperty(db);
      if (cancelled) return;
      setProperty(p);
      if (!p) {
        setTuneBadge(0);
        return;
      }
      // Mirror My Home: blank profile fields + systems missing a
      // subtype pick + seasonal systems pending review add up to the
      // "things to tune" count. Wave 43 added the seasonal third
      // component so the badge matches the queue length.
      const blanks = profileAttributeChecks(p).filter((c) => !c.filled).length;
      const sub = await getSubtypeCoverageSignal(db, p.id);
      const subOpen = Math.max(0, sub.subtypeEligibleSystems - sub.subtypesSelected);
      const seasonalUnconfirmed = await getUnconfirmedSeasonalCount(db, p.id);
      if (!cancelled) setTuneBadge(blanks + subOpen + seasonalUnconfirmed);
    })();
    return () => { cancelled = true; };
  }, [db]);

  const togglePref = async (key: PrefKey) => {
    if (!property) return;
    const next = property[key] === 1 ? 0 : 1;
    setProperty({ ...property, [key]: next });
    await db.runAsync(
      `UPDATE property SET ${key} = ?, updated_at = ? WHERE id = ?`,
      next,
      now(),
      property.id
    );
    // Bump the data-revision so subscribers (AppHeader for the ticker
    // toggle, RightRail / DesktopSidebar for any property-derived widget)
    // re-read instead of staying on the stale cached value. Without this
    // the ticker toggle reads correctly in Settings but the ticker itself
    // never reacts until a manual refresh.
    bumpDataRevision();
  };

  // Desktop sidebar widget order + visibility — a JSON array of keys on
  // the property. The array IS the order, and presence = enabled. Optimistic
  // update + immediate persist + bumpDataRevision so the RightRail picks
  // up the change without waiting for the next nav.
  const sidebarOrder = parseWidgetKeys(property?.sidebar_widgets_json);
  const sidebarEnabled = new Set(sidebarOrder);

  const persistSidebar = async (next: string[]) => {
    if (!property) return;
    const json = JSON.stringify(next);
    setProperty({ ...property, sidebar_widgets_json: json });
    await db.runAsync(
      `UPDATE property SET sidebar_widgets_json = ?, updated_at = ? WHERE id = ?`,
      json,
      now(),
      property.id
    );
    bumpDataRevision();
  };

  const toggleSidebarWidget = async (key: string) => {
    const current = sidebarOrder.slice();
    const idx = current.indexOf(key);
    if (idx >= 0) {
      current.splice(idx, 1);
    } else {
      // Newly enabled widgets append to the end so they don't surprise
      // the user by jumping ahead of already-arranged ones.
      current.push(key);
    }
    await persistSidebar(current);
  };

  const moveSidebarWidget = async (key: string, delta: -1 | 1) => {
    const current = sidebarOrder.slice();
    const idx = current.indexOf(key);
    if (idx < 0) return;
    const target = idx + delta;
    if (target < 0 || target >= current.length) return;
    [current[idx], current[target]] = [current[target], current[idx]];
    await persistSidebar(current);
  };

  const reset = async () => {
    setWorking(true);
    try {
      await resetPropertyData(db);
      useOnboarding.getState().reset();
      router.replace('/(onboarding)/welcome' as never);
    } finally {
      setWorking(false);
    }
  };

  const confirmReset = async () => {
    const ok = await confirm({
      title: 'Reset Homestead?',
      message: 'This deletes your property, systems, and the year-plan. You can rebuild from scratch.',
      destructive: true,
      confirmLabel: 'Reset everything',
    });
    if (ok) await reset();
  };

  const exportIcs = async () => {
    setBusy('ics');
    try {
      const prop = await getActiveProperty(db);
      if (!prop) return flash('No property set up yet.');
      const tasks = await getActiveTasks(db, prop.id, 500);
      const res = await exportTasksToIcs(tasks, prop.name ?? undefined);
      flash(res.success ? `Exported ${tasks.length} tasks.` : `Failed: ${res.error}`);
    } finally {
      setBusy(null);
    }
  };

  const exportPdf = async () => {
    setBusy('pdf');
    try {
      const prop = await getActiveProperty(db);
      if (!prop) return flash('No property set up yet.');
      const [systems, contacts, documents, history, inventory, notesList, quotes] = await Promise.all([
        getSystemsForProperty(db, prop.id),
        listContacts(db, prop.id),
        listDocumentsForProperty(db, prop.id),
        getServiceHistoryForProperty(db, prop.id, 500),
        listInventory(db, prop.id),
        listNotes(db, prop.id, 500),
        listQuotes(db, prop.id),
      ]);
      const res = await exportBinderPdf({
        property: prop,
        systems,
        contacts,
        documents,
        history,
        inventory,
        notes: notesList,
        quotes,
      });
      // ALWAYS register the file as a Document if the render succeeded.
      // Gives Android users a reliable retrieval path (Media & Files) even
      // if the OS share sheet failed or they dismissed it.
      if (res.success && res.fileUri && Platform.OS !== 'web') {
        try {
          await addDocument(db, {
            propertyId: prop.id,
            kind: 'manual',
            title: `Home Record · ${new Date().toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}`,
            file_uri: res.fileUri,
            mime_type: 'application/pdf',
          });
        } catch (err) {
          console.warn('Failed to register PDF as Document', err);
        }
      }
      flash(
        res.success && !res.error
          ? 'PDF saved to Media & Files.'
          : res.success
          ? res.error ?? 'PDF saved.'
          : `Failed: ${res.error}`
      );
    } finally {
      setBusy(null);
    }
  };

  const replan = async () => {
    setBusy('replan');
    try {
      const prop = await getActiveProperty(db);
      if (!prop) return;
      setReplanHomeName(prop.name?.trim() || 'this home');
      setReplanPropertyId(prop.id);
      setReplanLoading(true);
      setReplanSummary({ added: 0, removed: 0, rescheduled: 0, addedTitles: [], removedTitles: [], rescheduledTitles: [] });
      const { previewReplanDiff } = await import('@/lib/tasks');
      const diff = await previewReplanDiff(db, prop.id);
      setReplanSummary(diff);
      setReplanLoading(false);
    } finally {
      setBusy(null);
    }
  };

  const onCheckUpdates = async () => {
    setBusy('update-check');
    const res = await checkAndApplyUpdate();
    setBusy(null);
    switch (res.kind) {
      case 'web':
        // Active reload bypasses the browser's HTTP cache, equivalent to
        // Ctrl+Shift+R — fetches the latest static bundle from the CDN.
        // We do it explicitly so the "Check for updates" button feels real
        // on web instead of just flashing a "go refresh yourself" toast.
        if (Platform.OS === 'web' && typeof window !== 'undefined') {
          flash('Reloading for the latest…');
          setTimeout(() => window.location.reload(), 400);
        } else {
          flash('Web auto-updates on refresh.');
        }
        break;
      case 'no-runtime':
        flash('Updates not enabled in this build.');
        break;
      case 'up-to-date':
        flash("You're on the latest version.");
        break;
      case 'updated':
        flash('Update found — restarting now…');
        break;
      case 'failed':
        flash(`Couldn't check: ${res.message}`);
        break;
    }
  };

  const confirmReplan = async () => {
    const ok = await confirm({
      title: 'Replan your year?',
      message:
        "We'll throw out the still-pending tasks the planner created and rebuild them against today's home profile + systems. Your completed history, custom tasks, and snoozed items are kept. Use this when you've changed systems, climate, or lifestyle and want a fresh schedule.",
      confirmLabel: 'Replan',
    });
    if (ok) await replan();
  };

  const exportBackupFile = async () => {
    setBusy('export');
    try {
      const bundle = await exportBackup(db);
      const json = JSON.stringify(bundle, null, 2);
      const filename = backupFilename();
      if (Platform.OS === 'web') {
        // eslint-disable-next-line no-undef
        const blob = new Blob([json], { type: 'application/json' });
        // eslint-disable-next-line no-undef
        const url = URL.createObjectURL(blob);
        // eslint-disable-next-line no-undef
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        a.click();
        // eslint-disable-next-line no-undef
        setTimeout(() => URL.revokeObjectURL(url), 1500);
        flash('Backup downloaded.');
      } else {
        const file = new File(Paths.cache, filename);
        file.create();
        file.write(json);
        const canShare = await Sharing.isAvailableAsync();
        if (canShare) {
          await Sharing.shareAsync(file.uri, { mimeType: 'application/json' });
        } else {
          flash(`Saved to cache: ${file.uri}`);
        }
      }
    } catch (err) {
      console.warn('export backup failed', err);
      flash("Couldn't export backup.");
    } finally {
      setBusy(null);
    }
  };

  const importBackupFile = async () => {
    const ok = await confirm({
      title: 'Restore backup?',
      message:
        'This REPLACES all current data with the bundle’s contents. Export first if you want a safety net.',
      destructive: true,
      confirmLabel: 'Pick a file',
    });
    if (!ok) return;
    setBusy('import');
    try {
      const res = await DocumentPicker.getDocumentAsync({
        type: 'application/json',
        copyToCacheDirectory: true,
      });
      if (res.canceled || !res.assets[0]) return;
      let json: string;
      if (Platform.OS === 'web') {
        const r = await fetch(res.assets[0].uri);
        json = await r.text();
      } else {
        const f = new File(res.assets[0].uri);
        json = await f.text();
      }
      const parsed = JSON.parse(json);
      const result = await restoreBackup(db, parsed);
      if (!result.ok) {
        flash(`Restore failed: ${result.error}`);
        return;
      }
      const totalRows = Object.values(result.counts).reduce((a, b) => a + b, 0);
      flash(`Restored ${totalRows} rows.`);
      // Every surface is now stale — tell revision subscribers to refetch
      // so the app reflects the restored data without a manual reload.
      bumpDataRevision();
      const p = await getActiveProperty(db);
      if (p) setProperty(p);
    } catch (err) {
      console.warn('import backup failed', err);
      flash("Couldn't read that file.");
    } finally {
      setBusy(null);
    }
  };

  const clearCache = async () => {
    const ok = await confirm({
      title: 'Clear browser cache?',
      message:
        'Forgets cached pages, daily API budgets, and the React-Query/Service-Worker caches. Your Homestead data (in the SQLite database) is NOT touched.',
      confirmLabel: 'Clear',
    });
    if (!ok) return;
    setBusy('clear-cache');
    try {
      if (Platform.OS === 'web') {
        // eslint-disable-next-line no-undef
        if (typeof window !== 'undefined') {
          // Caches API — service worker caches
          // eslint-disable-next-line no-undef
          if (typeof window.caches !== 'undefined') {
            try {
              // eslint-disable-next-line no-undef
              const keys = await window.caches.keys();
              // eslint-disable-next-line no-undef
              await Promise.all(keys.map((k) => window.caches.delete(k)));
            } catch {
              // best effort
            }
          }
          // localStorage budgets + small prefs (NOT api keys, those are in
          // SecureStore on native and we don't want to nuke them on web either).
          try {
            // eslint-disable-next-line no-undef
            const keys = Object.keys(window.localStorage);
            for (const k of keys) {
              if (k.startsWith('homestead.quota.')) {
                // eslint-disable-next-line no-undef
                window.localStorage.removeItem(k);
              }
            }
          } catch {
            // best effort
          }
          // eslint-disable-next-line no-undef
          window.location.reload();
          return;
        }
      }
      flash('Cache cleared.');
    } finally {
      setBusy(null);
    }
  };

  const emailDigest = async () => {
    setBusy('digest');
    try {
      const prop = await getActiveProperty(db);
      if (!prop) return flash('No property set up yet.');
      const digest = await buildWeeklyDigest(db, prop);
      // Always show the preview sheet — gives the user Copy + Open options
      // even when the device has no mail handler (the bare mailto approach
      // silently did nothing on many web browsers).
      setDigestPreview(digest);
    } catch (err) {
      console.warn('emailDigest failed', err);
      flash("Couldn't build digest.");
    } finally {
      setBusy(null);
    }
  };

  // Tools grid was removed entirely from Settings — every nav-style
  // action it covered is reachable from the My Home Quick toolbar and
  // the header avatar menu. Settings is preferences / account / admin
  // only now. Keeping TOOLS + ToolDef around for one cycle in case
  // we want to reintroduce a curated grid later; mark intentionally
  // unused so the lint pass doesn't flag it.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const TOOLS: ToolDef[] = [
    // Color rotation chosen so no two adjacent tiles share a background —
    // gives the grid a more crafted, less repetitive look. Order mirrors
    // the usage-frequency priority decided above.
    //
    // Schedule leads the grid as the single most-used surface in the
    // app. The "Past tasks" tile used to sit further down — it was
    // really just /schedule with a Completed filter, so it merged in
    // here as a chip on the Schedule page itself.
    {
      key: 'schedule',
      label: 'Schedule',
      Icon: CalendarDays,
      bg: Palette.honeyDeep,
      onPress: () => router.push('/(tabs)/schedule' as never),
    },
    // "Systems" replaces the old "Search" tile here. Reasoning:
    //   - The global search button in the header (and the dedicated
    //     /search page) still exists for finding anything anywhere.
    //   - The Tools grid is the user's home base for managing their
    //     home — and the Systems page (with search, filter chips, and
    //     multi-select) is the deepest tool we have for that.
    //   - This tile is the canonical entry point into /systems
    //     alongside the My Home "View all" CTA.
    {
      key: 'systems',
      label: 'Systems',
      Icon: Home,
      bg: Palette.forest,
      onPress: () => router.push('/systems' as never),
    },
    // Color rotation rationale (full grid is 14 tiles / 2 rows × 7):
    //   Row 1: honeyDeep, forest, clay, forestLight, honey, moss, forestGlow
    //   Row 2: honey,     forestGlow, forest, moss, clay, honeyDeep, forestLight
    // All seven palette tints (forest / forestLight / forestGlow / moss
    // / honey / honeyDeep / clay) appear in each row. The rows are
    // shifted relative to each other so NO column repeats a color
    // vertically — every tile is distinct from its top/bottom neighbor
    // AND from its left/right neighbor. Emergency stays clay (semantic:
    // safety / critical) and Tune my plan stays honeyDeep (sparkle warmth);
    // everything else was assigned to satisfy the no-adjacent rule.
    {
      key: 'inventory',
      label: 'Inventory',
      Icon: Package,
      bg: Palette.clay,
      onPress: () => router.push('/inventory' as never),
    },
    {
      key: 'expenses',
      label: 'Expenses',
      Icon: Receipt,
      bg: Palette.forestLight,
      onPress: () => router.push('/expenses' as never),
    },
    {
      key: 'media',
      label: 'Files',
      Icon: FileImage,
      bg: Palette.honey,
      onPress: () => router.push('/media' as never),
    },
    {
      key: 'goals',
      label: 'Projects',
      Icon: Target,
      bg: Palette.moss,
      onPress: () => router.push('/projects' as never),
    },
    // Contacts swapped up ahead of Achievements so the row-1 tiles are
    // all "everyday-use" tools; Achievements is more of a discovery /
    // engagement surface and now leads row 2.
    {
      key: 'contacts',
      label: 'Contacts',
      Icon: Users,
      bg: Palette.forestGlow,
      onPress: () => router.push('/contacts' as never),
    },
    // Achievements / Find a Pro / Timeline / PDF binder / Emergency /
    // Tune my plan / Replan year moved to the My Home → Resources
    // section. The split: Settings Tools = navigation to data stores
    // (Schedule, Systems, Inventory, etc.). My Home Resources = things
    // you do FOR a specific home (replan it, export its binder, etc.).
    // The seven now-Resources tiles are still reachable globally via
    // their existing routes — the My Home Resources grid is just their
    // canonical surface.
  ];

  return (
    <View style={styles.container}>
      <ScrollView ref={settingsScrollRef} contentContainerStyle={{ paddingBottom: 110 }}>
        <View style={styles.header}>
          <View style={styles.titleRow}>
            <View style={{ flex: 1, minWidth: 0 }}>
              <Text style={styles.h1}>Settings</Text>
              <Text style={styles.sub}>Tune Homestead to your home and life.</Text>
            </View>
            {/* Submenu — mirrors My Home / Schedule / Today for cross-
                page consistency. Same icons + labels everywhere so the
                user always finds Edit, Homes, Tune, Replan in the same
                upper-right spot. */}
            <View style={styles.titleIcons}>
              {/* Edit removed (Wave 61, user call) — Home settings below
                  and the Homes manager both reach the editor. */}
              <SettingsIconBtn
                Icon={Home}
                label="Homes"
                accessibilityLabel="Open Home Manager"
                onPress={() => router.push('/homes' as never)}
              />
              <SettingsIconBtn
                Icon={Sparkles}
                label="Tune"
                accessibilityLabel="Tune my plan"
                tone={tuneBadge > 0 ? 'honey' : 'forest'}
                badge={tuneBadge}
                onPress={() => router.push('/profile-setup' as never)}
              />
              <SettingsIconBtn
                Icon={RefreshCw}
                label="Replan"
                accessibilityLabel={
                  property?.needs_replan === 1
                    ? 'Replan year — changes pending'
                    : 'Refresh plan'
                }
                tone={property?.needs_replan === 1 ? 'honey' : 'forest'}
                badge={property?.needs_replan === 1 ? 1 : undefined}
                onPress={replan}
              />
            </View>
          </View>
        </View>

        {/*
          Tools grid was removed. Every nav-style action it covered
          (Schedule, Systems, Inventory, etc.) is reachable from the
          Quick toolbar on My Home and the main menu (header avatar).
          Settings is now strictly for preferences/account/admin — not
          a nav grid.
        */}

        <SectionHeader>My account</SectionHeader>
        <Card>
          <NavRow
            label="Account"
            sub="Sign-in, profile, cloud sync, subscription"
            Icon={User}
            onPress={() => router.push('/account' as never)}
          />
        </Card>

        <SectionHeader>My home</SectionHeader>
        <Card>
          {/* Home Manager leads the section — multi-home is the
              superset (manages all homes including the active one),
              so a user looking for "do something with my home" naturally
              starts there. Edit my home + Add a system act on the
              currently active home and sit below as the more-focused
              follow-ups. */}
          <NavRow
            label="Home Manager"
            sub="Switch, edit, add, or remove any of your homes"
            Icon={LayoutGrid}
            onPress={() => router.push('/homes' as never)}
          />
          <NavRow
            label="Edit my home"
            sub="Bedrooms, basement, roof, water, garage — photo + more"
            Icon={Home}
            onPress={() => router.push('/property/edit' as never)}
          />
          <NavRow
            label="Add a system or equipment"
            sub="52 categories, multi-instance, with subtypes"
            Icon={Plus}
            onPress={() => router.push('/system/new' as never)}
            last
          />
        </Card>

        <SectionHeader>Preferences</SectionHeader>
        <Card>
          <NavRow
            label="Your name"
            sub={
              property?.owner_first_name
                ? `${property.owner_first_name}${property.owner_last_name ? ' ' + property.owner_last_name : ''} — used in the home greeting`
                : 'Set a first name and we’ll greet you by it on the home screen'
            }
            Icon={User}
            onPress={openNameEditor}
          />
          <NavRow
            label="Greet me by…"
            sub={
              (property?.greeting_source ?? 'first_name') === 'home_name'
                ? `Home name (${property?.name?.trim() || 'set in Edit my home'})`
                : `First name (${property?.owner_first_name?.trim() || 'set above'})`
            }
            Icon={Sparkles}
            onPress={() => setGreetingOpen(true)}
          />
          <NavRow
            label="Notifications"
            sub="Daily or weekly digest, plus an optional overdue nudge"
            Icon={BellRing}
            onPress={() => router.push('/notifications' as never)}
          />
          <NavRow
            label={busy === 'digest' ? 'Building…' : 'Email this week’s digest'}
            sub="Preview the weekly task summary and send to yourself"
            Icon={Mail}
            onPress={emailDigest}
          />
          <ToggleRow
            label="Ask for cost when I finish a task"
            sub="The 'what did this cost?' prompt on Mark Done. Quick weekly chores already skip it automatically; turn off to skip it everywhere."
            Icon={Receipt}
            value={(property?.ask_expense_cost ?? 1) === 1}
            disabled={!property}
            onToggle={() => togglePref('ask_expense_cost')}
            last
          />
        </Card>

        <SectionHeader>View & layout</SectionHeader>
        <Card>
          <ToggleRow
            label="Show weather panel"
            sub="Today + frost watch + multi-day forecast on the home screen"
            Icon={CloudSun}
            value={(property?.show_weather ?? 1) === 1}
            disabled={!property}
            onToggle={() => togglePref('show_weather')}
          />
          <ToggleRow
            label="Show message ticker"
            sub="The single-line message bar under the top header. Bell icon stays either way."
            Icon={BellRing}
            value={(property?.show_message_ticker ?? 1) === 1}
            disabled={!property}
            onToggle={() => togglePref('show_message_ticker')}
          />
          <ToggleRow
            label="Show quick toolbar on Today"
            sub="The 4-tile shortcut row near the top of the Today tab. Turn off if you prefer to scroll straight to tasks."
            Icon={CloudSun}
            value={(property?.show_quick_toolbar ?? 1) === 1}
            disabled={!property}
            onToggle={() => togglePref('show_quick_toolbar')}
          />
          <ToggleRow
            label="Show daily calendar on Today"
            sub="The 7-day strip of upcoming tasks. Turn off for a calmer landing page."
            Icon={BellRing}
            value={(property?.show_daily_calendar ?? 1) === 1}
            disabled={!property}
            onToggle={() => togglePref('show_daily_calendar')}
          />
          <ToggleRow
            label="Show projects on Today"
            sub={'The "What you’re building toward" card. Turn off to keep Today focused on maintenance only.'}
            Icon={Target}
            value={(property?.show_projects_on_today ?? 1) === 1}
            disabled={!property}
            onToggle={() => togglePref('show_projects_on_today')}
          />
          <ToggleRow
            label="Monthly task mode"
            sub="Bundles every task to the 1st of its month. Quieter, batch-friendly — best for users who do home stuff in one big session. Pair with a monthly digest in Notifications if you want a single ping too."
            Icon={CalendarDays}
            value={(property?.monthly_task_mode ?? 0) === 1}
            disabled={!property}
            onToggle={async () => {
              await togglePref('monthly_task_mode');
              // The toggle flips a planner-affecting field, so set
              // needs_replan so the user gets a prompt to regenerate
              // their plan against the new mode (or wait for natural
              // Replan trigger).
              if (property) {
                await db.runAsync(
                  `UPDATE property SET needs_replan = 1 WHERE id = ?`,
                  property.id
                );
                bumpDataRevision();
              }
            }}
          />
          {/* Animated preloader + confetti toggles were removed — both are
              always on now. Module-level flags in Preloader.tsx and
              ConfettiOverlay.tsx default to true and are no longer mutated
              from the property prefs. */}
          <NavRow
            label="Manage My Home toolbar"
            sub="Pick which shortcuts appear above your systems on My Home"
            Icon={LayoutGrid}
            onPress={() => router.push('/quick-toolbar' as never)}
          />
          <NavRow
            label="Manage Quick Actions menu"
            sub="The + button menu — add, remove, reorder the actions"
            Icon={Plus}
            onPress={() => router.push('/quick-actions' as never)}
          />
          <NavRow
            label="Manage Tools menu"
            sub="The ☰ menu grid — pick your 8, reorder, see every tool"
            Icon={LayoutGrid}
            onPress={() => router.push('/tools-menu' as never)}
            last
          />
        </Card>

        {isDesktop ? (
          <>
            <SectionHeader>Sidebar widgets</SectionHeader>
            <Card>
              {/* Enabled widgets render first, in their saved order, with
                  up/down arrows to reorder. Disabled widgets sit below in
                  catalog order with just a toggle. The whole section only
                  shows on desktop where the sidebar actually displays. */}
              {sidebarOrder.map((key, i) => {
                const w = WIDGETS.find((x) => x.key === key);
                if (!w) return null;
                return (
                  <SidebarWidgetRow
                    key={w.key}
                    label={w.label}
                    sub={w.sub}
                    enabled
                    canMoveUp={i > 0}
                    canMoveDown={i < sidebarOrder.length - 1}
                    onMoveUp={() => moveSidebarWidget(w.key, -1)}
                    onMoveDown={() => moveSidebarWidget(w.key, 1)}
                    onToggle={() => toggleSidebarWidget(w.key)}
                    last={false}
                  />
                );
              })}
              {/* Wave 69: the catalog is growing — disabled widgets
                  collapse behind one "Add widgets" row so the section
                  stays as short as the user's actual sidebar. */}
              {(() => {
                const disabled = WIDGETS.filter((w) => !sidebarEnabled.has(w.key));
                if (disabled.length === 0) return null;
                return (
                  <>
                    <Pressable
                      onPress={() => setSidebarPickerOpen((v) => !v)}
                      style={({ pressed }) => [styles.widgetAddRow, pressed && { opacity: 0.8 }]}>
                      <Plus size={15} color={Palette.forest} strokeWidth={2.2} />
                      <Text style={styles.widgetAddText}>
                        {sidebarPickerOpen
                          ? 'Hide available widgets'
                          : `Add widgets · ${disabled.length} available`}
                      </Text>
                    </Pressable>
                    {sidebarPickerOpen
                      ? disabled.map((w, i, arr) => (
                          <SidebarWidgetRow
                            key={w.key}
                            label={w.label}
                            sub={w.sub}
                            enabled={false}
                            onToggle={() => toggleSidebarWidget(w.key)}
                            last={i === arr.length - 1}
                          />
                        ))
                      : null}
                  </>
                );
              })()}
            </Card>
          </>
        ) : null}

        <SectionHeader>App</SectionHeader>
        <Card>
          <NavRow
            label={busy === 'update-check' ? 'Checking…' : 'Check for updates'}
            sub={(() => {
              const v = getVersionInfo();
              const when = v.bundleDate
                ? v.bundleDate.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
                : 'baked in';
              return `Auto-checks on open. Now: v${v.runtimeVersion} · ${v.channel} · ${v.bundleId} (${when})`;
            })()}
            Icon={Rocket}
            onPress={onCheckUpdates}
            last
          />
        </Card>

        <SectionHeader>Backup & sync</SectionHeader>
        <Card>
          <NavRow
            label={busy === 'export' ? 'Exporting…' : 'Export backup (JSON)'}
            sub="Download a JSON file with every system, task, expense, goal, doc"
            Icon={Download}
            onPress={exportBackupFile}
          />
          <NavRow
            label={busy === 'import' ? 'Restoring…' : 'Restore from backup'}
            sub="Replace local data with a previously exported file"
            Icon={Upload}
            onPress={importBackupFile}
          />
          <NavRow
            label={busy === 'ics' ? 'Exporting…' : 'Export Calendar (.ics)'}
            sub="Drop all tasks into Apple Calendar, Google Calendar, Outlook"
            Icon={CalendarDays}
            onPress={exportIcs}
            last
          />
        </Card>

        {adminUnlocked ? (
          <>
            <SectionHeader>Admin</SectionHeader>
            <Card>
              <NavRow
                label="API keys"
                sub="Personal Google / weather keys override the server proxy. For devs and power users."
                Icon={Key}
                onPress={() => router.push('/api-keys' as never)}
              />
              <NavRow
                label={busy === 'clear-cache' ? 'Clearing…' : 'Clear browser cache'}
                sub="Forget cached pages + API budgets. Your Homestead data is untouched."
                Icon={RefreshCw}
                onPress={clearCache}
                last
              />
            </Card>
          </>
        ) : null}

        <SectionHeader>Coming soon</SectionHeader>
        <Card>
          <NavRow
            label="Account & cloud sync"
            sub="Multi-device + family sharing — pick a backend first (Supabase / PowerSync)"
            Icon={Users}
            soon
          />
          <NavRow
            label="Google Calendar 2-way sync"
            sub="Live sync vs the iCal export available today"
            Icon={CalendarDays}
            soon
          />
          <NavRow
            label="AI helper bot"
            sub="Ask anything about your home; opt-in cloud feature"
            Icon={Bot}
            soon
            last
          />
        </Card>

        <SectionHeader>Danger zone</SectionHeader>
        <Card>
          <Pressable
            onPress={confirmReset}
            disabled={working}
            style={({ pressed }) => pressed && { opacity: 0.85 }}>
            <NavRow
              label={working ? 'Resetting…' : 'Reset Homestead'}
              sub="Deletes property, systems, and the year-plan"
              Icon={RotateCcw}
              destructive
              last
            />
          </Pressable>
        </Card>

        <Pressable
          onPress={() => {
            if (adminUnlocked) return;
            const next = tapCount + 1;
            setTapCount(next);
            if (next >= 7) {
              adminUnlockedRef = true;
              setAdminUnlocked(true);
              flash('Admin tools unlocked.');
            } else if (next >= 4) {
              flash(`${7 - next} more…`);
            }
          }}>
          <Text style={styles.footer}>
            {(() => {
              const v = getVersionInfo();
              return `Homestead · v${v.runtimeVersion} · ${v.bundleId}${adminUnlocked ? ' · admin' : ''}`;
            })()}
          </Text>
        </Pressable>

        {toast ? (
          <View style={styles.toast}>
            <Text style={styles.toastText}>{toast}</Text>
          </View>
        ) : null}
      </ScrollView>

      <ReplanSummaryModal
        visible={replanSummary !== null}
        diff={replanSummary}
        loading={replanLoading}
        homeName={replanHomeName}
        onClose={() => {
          setReplanSummary(null);
          setReplanLoading(false);
        }}
        onConfirm={async () => {
          if (!replanPropertyId) return;
          try {
            await regenerateTasksWithDiff(db, replanPropertyId);
          } finally {
            setReplanSummary(null);
            setReplanLoading(false);
          }
        }}
      />

      <DigestPreview digest={digestPreview} onClose={() => setDigestPreview(null)} />

      <Modal animationType="fade" transparent visible={greetingOpen} onRequestClose={() => setGreetingOpen(false)}>
        <Pressable style={styles.modalBackdrop} onPress={() => setGreetingOpen(false)}>
          <Pressable style={styles.modalCard} onPress={(e) => e.stopPropagation()}>
            <Text style={styles.modalTitle}>Greet me by…</Text>
            <Text style={styles.modalSub}>
              Pick what shows on the home-screen greeting. If the chosen field
              is empty, we fall back to the other one.
            </Text>
            {(['first_name', 'home_name'] as const).map((src) => {
              const sel = (property?.greeting_source ?? 'first_name') === src;
              const label = src === 'first_name' ? 'My first name' : 'My home name';
              const hint =
                src === 'first_name'
                  ? property?.owner_first_name?.trim() || 'not set yet'
                  : property?.name?.trim() || 'not set yet';
              return (
                <Pressable
                  key={src}
                  onPress={() => setGreetingSource(src)}
                  style={({ pressed }) => [
                    styles.greetOpt,
                    sel && styles.greetOptSel,
                    pressed && { opacity: 0.85 },
                  ]}>
                  <View style={{ flex: 1 }}>
                    <Text style={[styles.greetOptLabel, sel && { color: Palette.forest }]}>{label}</Text>
                    <Text style={styles.greetOptHint}>{hint}</Text>
                  </View>
                  {sel ? <Check size={16} color={Palette.forest} strokeWidth={2.4} /> : null}
                </Pressable>
              );
            })}
          </Pressable>
        </Pressable>
      </Modal>

      <Modal animationType="fade" transparent visible={nameOpen} onRequestClose={() => setNameOpen(false)}>
        <Pressable style={styles.modalBackdrop} onPress={() => setNameOpen(false)}>
          <Pressable style={styles.modalCard} onPress={(e) => e.stopPropagation()}>
            <Text style={styles.modalTitle}>Your name</Text>
            <Text style={styles.modalSub}>
              We&apos;ll greet you by first name on the home screen. Last name is optional.
            </Text>
            <TextInput
              value={editFirst}
              onChangeText={setEditFirst}
              placeholder="First name"
              placeholderTextColor={Palette.inkFaint}
              autoCapitalize="words"
              style={[styles.modalInput, webNoOutline]}
            />
            <TextInput
              value={editLast}
              onChangeText={setEditLast}
              placeholder="Last (optional)"
              placeholderTextColor={Palette.inkFaint}
              autoCapitalize="words"
              style={[styles.modalInput, webNoOutline, { marginTop: 8 }]}
            />
            <View style={styles.modalActions}>
              <Pressable
                onPress={() => setNameOpen(false)}
                style={({ pressed }) => [styles.modalBtnGhost, pressed && { opacity: 0.7 }]}>
                <Text style={styles.modalBtnGhostText}>Cancel</Text>
              </Pressable>
              <Pressable
                onPress={saveName}
                style={({ pressed }) => [styles.modalBtn, pressed && { opacity: 0.85 }]}>
                <Text style={styles.modalBtnText}>Save</Text>
              </Pressable>
            </View>
          </Pressable>
        </Pressable>
      </Modal>
    </View>
  );
}

/**
 * Settings submenu icon. Same icon+label stack as My Home / Schedule /
 * Today. Forest icon + forest label so it reads as a green-themed
 * affordance row, matching the Schedule submenu styling the user
 * picked as the canonical look.
 */
function SettingsIconBtn({
  Icon,
  label,
  accessibilityLabel,
  onPress,
  tone = 'forest',
  badge,
}: {
  Icon: ComponentType<{ size?: number; color?: string; strokeWidth?: number }>;
  label: string;
  accessibilityLabel: string;
  onPress: () => void;
  tone?: 'forest' | 'honey';
  badge?: number;
}) {
  const color = tone === 'honey' ? Palette.honeyDeep : Palette.forest;
  return (
    <Pressable
      onPress={onPress}
      accessibilityLabel={accessibilityLabel}
      hitSlop={6}
      style={({ pressed }) => [
        styles.titleIconStack,
        pressed && { transform: [{ scale: 0.94 }], opacity: 0.85 },
      ]}>
      <View style={styles.titleIconBtn}>
        <Icon size={17} color={color} strokeWidth={1.9} />
        {badge && badge > 0 ? (
          <View style={styles.titleIconBadge}>
            <Text style={styles.titleIconBadgeText}>
              {badge > 9 ? '9+' : String(badge)}
            </Text>
          </View>
        ) : null}
      </View>
      <Text style={styles.titleIconLabel} numberOfLines={1}>
        {label}
      </Text>
    </Pressable>
  );
}

function SectionHeader({ children }: { children: string }) {
  return <Text style={styles.section}>{children.toUpperCase()}</Text>;
}

function Card({ children }: { children: React.ReactNode }) {
  return <View style={styles.card}>{children}</View>;
}

function NavRow({
  label,
  sub,
  Icon,
  onPress,
  soon,
  destructive,
  last,
}: {
  label: string;
  sub?: string;
  Icon: ComponentType<{ size?: number; color?: string; strokeWidth?: number }>;
  onPress?: () => void;
  soon?: boolean;
  destructive?: boolean;
  last?: boolean;
}) {
  const body = (
    <View style={[styles.row, last && { borderBottomWidth: 0 }]}>
      <View
        style={[
          styles.rowIcon,
          destructive && { backgroundColor: 'rgba(176,95,60,0.15)' },
        ]}>
        <Icon size={18} color={destructive ? Palette.clay : Palette.forest} strokeWidth={1.7} />
      </View>
      <View style={{ flex: 1 }}>
        <Text style={[styles.rowLabel, destructive && { color: Palette.clay }]}>{label}</Text>
        {sub ? <Text style={styles.rowSub}>{sub}</Text> : null}
      </View>
      {soon ? (
        <Text style={styles.soonTag}>SOON</Text>
      ) : (
        <ChevronRight size={18} color={Palette.inkFaint} strokeWidth={1.7} />
      )}
    </View>
  );
  if (!onPress) return body;
  return (
    <Pressable onPress={onPress} style={({ pressed }) => pressed && { opacity: 0.85 }}>
      {body}
    </Pressable>
  );
}

function ToggleRow({
  label,
  sub,
  Icon,
  value,
  onToggle,
  disabled,
  last,
}: {
  label: string;
  sub?: string;
  Icon: ComponentType<{ size?: number; color?: string; strokeWidth?: number }>;
  value: boolean;
  onToggle: () => void;
  disabled?: boolean;
  last?: boolean;
}) {
  return (
    <Pressable
      onPress={onToggle}
      disabled={disabled}
      style={({ pressed }) => [pressed && { opacity: 0.85 }, disabled && { opacity: 0.5 }]}>
      <View style={[styles.row, last && { borderBottomWidth: 0 }]}>
        <View style={styles.rowIcon}>
          <Icon size={18} color={Palette.forest} strokeWidth={1.7} />
        </View>
        <View style={{ flex: 1 }}>
          <Text style={styles.rowLabel}>{label}</Text>
          {sub ? <Text style={styles.rowSub}>{sub}</Text> : null}
        </View>
        <View style={[styles.toggle, value && styles.toggleOn]}>
          <View style={[styles.toggleThumb, value && styles.toggleThumbOn]} />
        </View>
      </View>
    </Pressable>
  );
}

function SidebarWidgetRow({
  label,
  sub,
  enabled,
  canMoveUp,
  canMoveDown,
  onMoveUp,
  onMoveDown,
  onToggle,
  last,
}: {
  label: string;
  sub: string;
  enabled: boolean;
  canMoveUp?: boolean;
  canMoveDown?: boolean;
  onMoveUp?: () => void;
  onMoveDown?: () => void;
  onToggle: () => void;
  last?: boolean;
}) {
  return (
    <View style={[styles.row, last && { borderBottomWidth: 0 }]}>
      <View style={styles.rowIcon}>
        <LayoutGrid size={18} color={Palette.forest} strokeWidth={1.7} />
      </View>
      <View style={{ flex: 1, minWidth: 0 }}>
        <Text style={styles.rowLabel}>{label}</Text>
        <Text style={styles.rowSub}>{sub}</Text>
      </View>
      {/* Up/down arrows show only for enabled widgets — disabled
          widgets have no order to express until they're turned on. */}
      {enabled ? (
        <View style={styles.reorderCol}>
          <Pressable
            onPress={onMoveUp}
            disabled={!canMoveUp}
            hitSlop={6}
            style={({ pressed }) => [
              styles.reorderBtn,
              !canMoveUp && { opacity: 0.25 },
              pressed && canMoveUp && { opacity: 0.6 },
            ]}>
            <ArrowUp size={13} color={Palette.ink} strokeWidth={2} />
          </Pressable>
          <Pressable
            onPress={onMoveDown}
            disabled={!canMoveDown}
            hitSlop={6}
            style={({ pressed }) => [
              styles.reorderBtn,
              !canMoveDown && { opacity: 0.25 },
              pressed && canMoveDown && { opacity: 0.6 },
            ]}>
            <ArrowDown size={13} color={Palette.ink} strokeWidth={2} />
          </Pressable>
        </View>
      ) : null}
      <Pressable
        onPress={onToggle}
        hitSlop={8}
        style={({ pressed }) => [pressed && { opacity: 0.85 }]}>
        <View style={[styles.toggle, enabled && styles.toggleOn]}>
          <View style={[styles.toggleThumb, enabled && styles.toggleThumbOn]} />
        </View>
      </Pressable>
    </View>
  );
}

const styles = StyleSheet.create({
  widgetAddRow: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'center',
    gap: 6, paddingVertical: 13,
    borderTopWidth: 1, borderTopColor: Palette.line,
  },
  widgetAddText: {
    fontFamily: Fonts.sansBold, fontSize: 13, color: Palette.forest,
  },
  container: { flex: 1, backgroundColor: Palette.sand },
  header: { paddingHorizontal: 22, paddingTop: Spacing.three },
  h1: { fontFamily: Fonts.serif, fontSize: 27, color: Palette.ink, letterSpacing: -0.3 },
  sub: { marginTop: 3, fontSize: 13, color: Palette.inkSoft, fontFamily: Fonts.sansMedium },
  // Settings submenu — matches My Home / Schedule / Today pattern.
  // Forest icons + forest labels for a green-themed affordance row.
  titleRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 12,
  },
  titleIcons: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 4,
  },
  titleIconStack: {
    alignItems: 'center',
    gap: 3,
    paddingHorizontal: 2,
  },
  titleIconBtn: {
    width: 34,
    height: 34,
    borderRadius: 17,
    borderWidth: 1,
    borderColor: Palette.line,
    backgroundColor: Palette.card,
    alignItems: 'center',
    justifyContent: 'center',
  },
  // Tiny honey-tinted pill in the upper-right of the icon button —
  // mirrors My Home / Today so the affordance reads identically.
  titleIconBadge: {
    position: 'absolute',
    top: -3,
    right: -3,
    minWidth: 16,
    height: 16,
    borderRadius: 8,
    paddingHorizontal: 4,
    backgroundColor: Palette.honeyDeep,
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1.5,
    borderColor: Palette.sand,
  },
  titleIconBadgeText: {
    fontFamily: Fonts.sansBold,
    fontSize: 10.5,
    lineHeight: 11,
    color: Palette.cream,
  },
  titleIconLabel: {
    fontFamily: Fonts.sansBold,
    fontSize: 11,
    lineHeight: 11,
    color: Palette.forest,
    letterSpacing: 0.2,
    maxWidth: 60,
    textAlign: 'center',
  },
  section: {
    marginTop: Spacing.five, marginHorizontal: 22,
    fontSize: 12, fontFamily: Fonts.sansBold, color: Palette.inkFaint,
    letterSpacing: 0.8, marginBottom: 8,
  },
  toolGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    paddingHorizontal: 14,
    // Centered rows. Previously was flex-start (left-aligned), which
    // meant 7×15% tiles on web left a big slack on the right edge and
    // 4×22% tiles on mobile did the same. Centering distributes that
    // slack evenly on both sides — full rows look balanced; the
    // trailing partial row still clusters together (centered as a
    // group) instead of spreading like space-between would. Best of
    // both worlds.
    justifyContent: 'center',
    columnGap: 8,
    rowGap: 10,
  },
  toolTile: {
    // Width is provided inline (22% mobile / 15% desktop) so the same
    // tile style works at both breakpoints. We intentionally do NOT set
    // minWidth here — on the narrowest phones (≤360 css px) a minWidth
    // would force overflow rather than letting the tile shrink to fit.
    aspectRatio: 1,
    backgroundColor: Palette.card,
    borderWidth: 1,
    borderColor: Palette.line,
    borderRadius: 14,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 4,
    paddingVertical: 8,
    gap: 6,
  },
  // Card-on-hover: warm bg shift + forest-tinted border + tiny lift.
  // Reads as "I'm a tappable card" without breaking the calm grid.
  toolTileHover: {
    backgroundColor: '#FBF6EA',
    borderColor: Palette.forestLight,
    transform: [{ translateY: -1 }],
  },
  toolIcon: {
    width: 40,
    height: 40,
    borderRadius: 20,
    alignItems: 'center',
    justifyContent: 'center',
  },
  toolLabel: {
    fontFamily: Fonts.sansBold,
    fontSize: 11.5,
    color: Palette.ink,
    textAlign: 'center',
    lineHeight: 13,
  },
  card: {
    marginHorizontal: 18, backgroundColor: Palette.card,
    borderWidth: 1, borderColor: Palette.line, borderRadius: Radius.xl,
    paddingHorizontal: 16,
  },
  row: {
    flexDirection: 'row', alignItems: 'center', gap: 12,
    paddingVertical: 14, borderBottomWidth: 1, borderBottomColor: Palette.line,
  },
  rowIcon: {
    width: 36, height: 36, borderRadius: 11,
    backgroundColor: '#EAF0EA',
    alignItems: 'center', justifyContent: 'center',
  },
  rowLabel: { fontSize: 14, fontFamily: Fonts.sansBold, color: Palette.ink },
  rowSub: {
    marginTop: 1, fontSize: 13, fontFamily: Fonts.sansMedium,
    color: Palette.inkSoft, lineHeight: 17,
  },
  soonTag: {
    fontSize: 11, fontFamily: Fonts.sansBold, color: Palette.honeyDeep,
    backgroundColor: Palette.honeySoft,
    paddingHorizontal: 6, paddingVertical: 3, borderRadius: 6, letterSpacing: 0.5,
  },
  footer: {
    marginTop: Spacing.five, textAlign: 'center',
    fontSize: 12, fontFamily: Fonts.sansSemibold, color: Palette.inkFaint,
  },
  toast: {
    marginTop: Spacing.four, marginHorizontal: Spacing.four,
    padding: 13, borderRadius: 12, backgroundColor: Palette.forest,
  },
  toastText: { color: Palette.cream, fontFamily: Fonts.sansBold, fontSize: 13.5 },
  reorderCol: {
    flexDirection: 'column',
    gap: 4,
    marginRight: 8,
  },
  reorderBtn: {
    width: 28, height: 22, borderRadius: 8,
    borderWidth: 1, borderColor: Palette.line,
    alignItems: 'center', justifyContent: 'center',
    backgroundColor: Palette.sand,
  },
  toggle: {
    width: 44, height: 26, borderRadius: 13,
    backgroundColor: Palette.sandDeep, padding: 3, justifyContent: 'center',
  },
  toggleOn: { backgroundColor: Palette.forest },
  toggleThumb: { width: 20, height: 20, borderRadius: 10, backgroundColor: Palette.cream },
  toggleThumbOn: { transform: [{ translateX: 18 }] },
  modalBackdrop: {
    flex: 1, backgroundColor: 'rgba(0,0,0,0.45)',
    alignItems: 'center', justifyContent: 'center', padding: 20,
  },
  modalCard: {
    backgroundColor: Palette.sand, borderRadius: 24,
    padding: 20, width: '100%', maxWidth: 380,
  },
  modalTitle: { fontFamily: Fonts.serifBold, fontSize: 18, color: Palette.ink },
  modalSub: {
    marginTop: 4, marginBottom: 14,
    fontFamily: Fonts.sansMedium, fontSize: 13.5, color: Palette.inkSoft, lineHeight: 17,
  },
  modalInput: {
    borderWidth: 1.5, borderColor: Palette.line,
    backgroundColor: Palette.card, borderRadius: 11,
    paddingHorizontal: 12, paddingVertical: 11,
    fontFamily: Fonts.sansSemibold, fontSize: 14, color: Palette.ink,
  },
  modalActions: {
    flexDirection: 'row', gap: 10, marginTop: 16, justifyContent: 'flex-end',
  },
  modalBtn: {
    backgroundColor: Palette.forest,
    paddingHorizontal: 18, paddingVertical: 11, borderRadius: 12,
  },
  modalBtnText: { color: Palette.cream, fontFamily: Fonts.sansBold, fontSize: 13.5 },
  modalBtnGhost: {
    paddingHorizontal: 14, paddingVertical: 11, borderRadius: 12,
  },
  modalBtnGhostText: { color: Palette.inkSoft, fontFamily: Fonts.sansBold, fontSize: 13.5 },
  greetOpt: {
    flexDirection: 'row', alignItems: 'center', gap: 10,
    paddingVertical: 12, paddingHorizontal: 14,
    borderRadius: 12, marginTop: 8,
    backgroundColor: Palette.card,
    borderWidth: 1.5, borderColor: Palette.line,
  },
  greetOptSel: { borderColor: Palette.forest, backgroundColor: '#EEF3EE' },
  greetOptLabel: { fontFamily: Fonts.sansBold, fontSize: 14, color: Palette.ink },
  greetOptHint: {
    marginTop: 2,
    fontFamily: Fonts.sansMedium, fontSize: 12.5, color: Palette.inkSoft,
  },
});
